import Button from '@mui/material/Button';
import React, { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import defaultSearchCountry from '../../../forms/HeroSearchForm/defaultSearchCountry';
import routeConfiguration from '../../../routeConfiguration';
import { neutral } from '../../../styles/mui/colors';
import { createResourceLocatorString } from '../../../util/routes';
import IconList from '../../Icons/IconList';
import IconMap from '../../Icons/IconMap';

const toMapButtonStyle = {
  borderRadius: 200,
  bgcolor: 'white',
  borderColor: neutral[200],
  '&:hover': {
    bgcolor: 'white',
  },
  '&:active': {
    bgcolor: 'white',
  },
  '&:focus': {
    bgcolor: 'white',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  color: 'inherit',
};
/**
 * ToggleMapButtonDesktopComponent is a button that toggles between showing a map or a list view on desktop devices.
 * Depending on the `shouldShowSearchMap` prop, it displays either a button to go back to the listings or to open the map view.
 *
 * @component
 *
 * @param {object} props - The component props.
 * @param {Function} props.onListIconClick - Callback function to handle the click event when switching to the list view.
 * @param {Function} props.onMapIconClick - Callback function to handle the click event when switching to the map view.
 * @param {boolean} props.shouldShowSearchMap - Determines whether the map view is currently shown or not.
 *
 * @returns {JSX.Element} A button that toggles between map and list views.
 */
const ToggleMapButtonDesktopComponent = ({
  onListIconClick,
  onMapIconClick,
  shouldShowSearchMap,
  isLandingPage = false,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const handleSubmit = useCallback(
    ({ location, dates, state }) => {
      const searchParams = {
        dates: dates,
        address: location ? location.address : '',
        bounds: location ? location.bounds : '',
      };
      if (location || dates) {
        navigate(
          createResourceLocatorString(
            'SearchPage',
            routeConfiguration(),
            {},
            { ...searchParams, ...state },
            intl.locale
          ),
          { state }
        );
      }
    },
    [navigate, intl.locale]
  );
  if (isLandingPage) {
    return (
      <Button
        autoFocus={false}
        onClick={() => {
          handleSubmit({
            dates: null,
            location: defaultSearchCountry(intl.locale),
            state: { tab: 'map' },
          });
        }}
        startIcon={<IconMap />}
        sx={{
          ...toMapButtonStyle,
        }}
        variant="outlined"
      >
        <FormattedMessage id="SearchFiltersMobile.openMapView" />
      </Button>
    );
  }

  return shouldShowSearchMap ? (
    <Button
      autoFocus={false}
      onClick={onListIconClick}
      startIcon={<IconList />}
      sx={{
        ...toMapButtonStyle,
      }}
      variant="outlined"
    >
      <FormattedMessage id="SearchPage.backToListingsButton" />
    </Button>
  ) : (
    <Button
      autoFocus={false}
      onClick={onMapIconClick}
      startIcon={<IconMap />}
      sx={{
        ...toMapButtonStyle,
      }}
      variant="outlined"
    >
      <FormattedMessage id="SearchFiltersMobile.openMapView" />
    </Button>
  );
};

ToggleMapButtonDesktopComponent.displayName = 'ToggleMapButtonDesktop';
const ToggleMapButtonDesktop = memo(ToggleMapButtonDesktopComponent);
export default ToggleMapButtonDesktop;
